import { useState, useEffect, useCallback, useRef } from 'react';

const RETRY_DELAY_MS = 5000; // 5 seconds between retries

export const useIframeError = (sandboxHost, iframeRef, setInput, handleProgrammaticSubmit) => {
  const [previewError, setPreviewError] = useState(null);
  const [loadingState, setLoadingState] = useState('loading'); // 'loading' | 'loaded'
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);

  // Clear retry state
  const clearRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    retryCountRef.current = 0;
  }, []);

  // Schedule automatic retry on iframe error
  const scheduleRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    console.log(`[PreviewPanel] Retry ${++retryCountRef.current} in 5s`);

    retryTimeoutRef.current = setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.src = iframeRef.current.src;
      }
      scheduleRetry(); // Continue the loop
    }, RETRY_DELAY_MS);
  }, [iframeRef]);

  // Setup iframe error detection (for runtime errors) and ready signal detection
  // Always listen for messages, regardless of iframe mount state
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return;

      // Handle runtime console errors
      if (event.data.type === 'console-error') {
        console.log('[PreviewPanel] Console error received');
        setPreviewError(event.data.error);
      }

      // Handle ready signals (both types do the same thing)
      if (event.data.type === 'kolega-ready' || event.data.type === 'kolega-fully-loaded') {
        console.log('[PreviewPanel] Ready signal received');
        setLoadingState('loaded');
        clearRetry();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [clearRetry]); // Add clearRetry to dependencies

  // Initialize loading state when sandboxHost changes
  useEffect(() => {
    clearRetry();

    if (!sandboxHost) return;

    setLoadingState('loading');
    console.log('[PreviewPanel] Loading preview for:', sandboxHost);

    // Start retry loop immediately
    scheduleRetry();

    return () => {
      clearRetry();
    };
  }, [sandboxHost, clearRetry, scheduleRetry]);

  const handleTryToFix = useCallback(() => {
    if (previewError && setInput) {
      const errorMessage = `I'm getting this error in my application:\n\n${previewError.message}${previewError.stack ? '\n\nStack trace:\n' + previewError.stack : ''}${previewError.filename ? '\n\nFile: ' + previewError.filename : ''}${previewError.line ? '\nLine: ' + previewError.line : ''}
      
Please help me fix this error.`;
      setInput(errorMessage);
      setPreviewError(null); // Clear the error after copying

      // Auto-submit immediately using the programmatic submitter
      if (typeof handleProgrammaticSubmit === 'function') {
        try {
          handleProgrammaticSubmit(errorMessage);
        } catch (e) {
          // Swallow to avoid breaking UI if submit flow changes
          console.error('Auto-submit after Try to Fix failed:', e);
        }
      }
    }
  }, [previewError, setInput, handleProgrammaticSubmit]);

  const handleIframeError = useCallback(() => {
    console.log('[PreviewPanel] Iframe error, starting retry');
    setPreviewError({
      message: 'Failed to load the preview. The sandbox might not be running or accessible.',
      stack: '',
      filename: '',
      line: null,
      column: null
    });
    scheduleRetry();
  }, [scheduleRetry]);

  const clearError = useCallback(() => {
    setPreviewError(null);
  }, []);

  // Reset loading state (for manual refresh)
  const resetLoadingState = useCallback(() => {
    console.log('[PreviewPanel] Manual refresh, resetting state');
    setLoadingState('loading');
    clearRetry();
    scheduleRetry();
  }, [clearRetry, scheduleRetry]);

  return {
    previewError,
    setPreviewError,
    handleTryToFix,
    handleIframeError,
    loadingState,
    resetLoadingState,
    clearError
  };
}; 